<script setup>
import { computed, ref, watch } from 'vue'
import { History, Sparkles, Trash2, X } from 'lucide-vue-next'

import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { NoResults } from '@/components/ui/empty-state'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import GlobalSearchPlatformSelect from './GlobalSearchPlatformSelect.vue'
import GlobalSearchResultsList from './GlobalSearchResultsList.vue'
import {
    CLEAR_RECENT_SEARCHES_COMMAND_VALUE,
    getCreatorCommandValue,
    SEARCH_MORE_CREATORS_COMMAND_VALUE,
    SHOW_MORE_RESULTS_COMMAND_VALUE,
} from './globalSearchCommandValues'
import { LOCAL_SEARCH_MIN_CHARS } from './useGlobalCreatorSearch'

const POPULAR_CREATOR_SEARCHES = ['MrBeast', 'Pokimane', 'Kai Cenat', 'Valkyrae', 'Ninja']

const props = defineProps({
    open: { type: Boolean, required: true },
    query: { type: String, required: true },
    platform: { type: String, required: true },
    recentSearches: { type: Array, required: true },
    visibleResults: { type: Array, required: true },
    pendingResults: { type: Array, required: true },
    isSearchableQuery: { type: Boolean, required: true },
    isInitialLoading: { type: Boolean, required: true },
    isLookingForCreators: { type: Boolean, required: true },
    canSearchMoreCreators: { type: Boolean, required: true },
    noCreatorsFound: { type: Boolean, required: true },
})

const emit = defineEmits([
    'update:open',
    'update:query',
    'update:platform',
    'clearSearch',
    'selectCreator',
    'selectSearchTerm',
    'removeRecentSearch',
    'clearRecentSearches',
    'searchMoreCreators',
    'showMoreResults',
])

const activeValue = ref('')
const highlightedValue = ref()
const searchShell = ref(null)
const preserveCommandItems = (values) => values

const openModel = computed({
    get: () => props.open,
    set: (value) => emit('update:open', value),
})

const queryModel = computed({
    get: () => props.query,
    set: (value) => emit('update:query', value),
})

const platformModel = computed({
    get: () => props.platform,
    set: (value) => emit('update:platform', value),
})

const hasQuery = computed(() => props.query.trim().length > 0)
const showRecentSearches = computed(() => !hasQuery.value && props.recentSearches.length > 0)
const showPopularSearches = computed(() => !hasQuery.value && props.recentSearches.length === 0)
const showResults = computed(() => hasQuery.value && props.isSearchableQuery)
const showMinimumPrompt = computed(() => hasQuery.value && !props.isSearchableQuery)
const keyboardCommandValues = computed(() => {
    if (showRecentSearches.value) {
        return [...props.recentSearches, CLEAR_RECENT_SEARCHES_COMMAND_VALUE]
    }

    if (showPopularSearches.value) {
        return POPULAR_CREATOR_SEARCHES
    }

    if (!showResults.value) return []

    const values = props.visibleResults.map(getCreatorCommandValue)

    if (props.canSearchMoreCreators) {
        values.push(SEARCH_MORE_CREATORS_COMMAND_VALUE)
    }

    if (props.pendingResults.length > 0) {
        values.push(SHOW_MORE_RESULTS_COMMAND_VALUE)
    }

    return values
})

const selectSearchTerm = (event, searchTerm) => {
    event?.preventDefault?.()
    activeValue.value = ''
    highlightedValue.value = undefined
    emit('selectSearchTerm', searchTerm)
    openModel.value = true
}

const clearSearch = () => {
    activeValue.value = ''
    highlightedValue.value = undefined
    emit('clearSearch')
    openModel.value = true
}

const clearRecentSearches = (event) => {
    event?.preventDefault?.()
    activeValue.value = ''
    highlightedValue.value = undefined
    emit('clearRecentSearches')
    openModel.value = true
}

const getCommandDisplayValue = () => queryModel.value

const moveHighlightedCommandValue = (direction) => {
    const values = keyboardCommandValues.value

    if (values.length === 0) return

    const currentIndex = values.findIndex((value) => value === highlightedValue.value)
    const nextIndex =
        currentIndex === -1
            ? direction > 0
                ? 0
                : values.length - 1
            : (currentIndex + direction + values.length) % values.length

    highlightedValue.value = values[nextIndex]
}

const onCommandInputKeydown = (event) => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return
    if (event.altKey || event.ctrlKey || event.metaKey) return

    event.preventDefault()
    event.stopPropagation()
    event.stopImmediatePropagation?.()

    openModel.value = true
    moveHighlightedCommandValue(event.key === 'ArrowDown' ? 1 : -1)
}

const onPopoverInteractOutside = (event) => {
    const target = event.detail?.originalEvent?.target

    if (target && searchShell.value?.contains(target)) {
        event.preventDefault()
    }
}

const onPlatformSelectOpenChange = (isOpen) => {
    if (isOpen) {
        openModel.value = false
    }
}

watch(
    () => [
        props.query,
        props.platform,
        props.visibleResults.length,
        props.pendingResults.length,
        props.canSearchMoreCreators,
        props.noCreatorsFound,
    ],
    () => {
        activeValue.value = ''
        highlightedValue.value = undefined
    },
)
</script>

<template>
    <Command
        v-model="activeValue"
        v-model:open="openModel"
        v-model:search-term="queryModel"
        v-model:selected-value="highlightedValue"
        :display-value="getCommandDisplayValue"
        :filter-function="preserveCommandItems"
        :reset-search-term-on-blur="false"
        :reset-search-term-on-select="false"
        class="overflow-visible rounded-none bg-transparent"
    >
        <Popover v-model:open="openModel">
            <PopoverAnchor as-child>
                <div
                    ref="searchShell"
                    class="relative flex h-9 w-[calc(100vw-108px)] items-center rounded border border-slate-300 bg-white transition-colors focus-within:border-slate-400 md:w-[400px]"
                >
                    <div
                        class="flex min-w-0 flex-1 items-center"
                        @click="openModel = true"
                    >
                        <CommandInput
                            id="global-creator-search"
                            v-model="queryModel"
                            name="global_creator_search"
                            type="search"
                            :auto-focus="false"
                            autocomplete="off"
                            placeholder="Search a creator by name..."
                            wrapper-class="h-9 min-w-0 flex-1 border-0 bg-transparent px-3"
                            class="h-9 appearance-none border-0 bg-transparent py-0 text-sm shadow-none outline-none ring-0 placeholder:text-slate-400 focus:border-transparent focus:outline-none focus:ring-0 focus-visible:ring-0"
                            @keydown.capture="onCommandInputKeydown"
                            @keydown.escape.stop="openModel = false"
                            @focus="openModel = true"
                        />
                        <button
                            v-if="hasQuery"
                            type="button"
                            class="mr-1 flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                            aria-label="Clear search"
                            @click.stop="clearSearch"
                        >
                            <X class="h-3.5 w-3.5" />
                        </button>
                    </div>
                    <div class="h-5 w-px shrink-0 bg-slate-200" />
                    <GlobalSearchPlatformSelect v-model="platformModel" @open-change="onPlatformSelectOpenChange" />
                </div>
            </PopoverAnchor>

            <PopoverContent
                align="start"
                :side-offset="8"
                class="w-[calc(100vw-108px)] max-w-[calc(100vw-24px)] overflow-hidden p-0 md:w-[400px]"
                @open-auto-focus.prevent
                @interact-outside="onPopoverInteractOutside"
            >
                <CommandList class="max-h-[440px]">
                    <CommandGroup v-if="showRecentSearches" heading="Recent searches" class="p-1">
                        <CommandItem
                            v-for="recentSearch in recentSearches"
                            :key="recentSearch"
                            :value="recentSearch"
                            class="group rounded px-2 py-2 data-[highlighted]:bg-slate-100"
                            @select="selectSearchTerm($event, recentSearch)"
                        >
                            <History class="mr-2 h-4 w-4 text-slate-400" />
                            <span class="min-w-0 flex-1 truncate text-sm text-slate-700">{{ recentSearch }}</span>
                            <button
                                type="button"
                                class="pointer-events-none ml-2 flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-slate-600 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100 focus:pointer-events-auto focus:opacity-100"
                                aria-label="Remove recent search"
                                @click.stop="emit('removeRecentSearch', recentSearch)"
                            >
                                <X class="h-3.5 w-3.5" />
                            </button>
                        </CommandItem>
                        <CommandItem
                            :value="CLEAR_RECENT_SEARCHES_COMMAND_VALUE"
                            class="mt-1 rounded px-2 py-2 text-xs text-slate-500 data-[highlighted]:bg-rose-50 data-[highlighted]:text-rose-600"
                            @select="clearRecentSearches"
                        >
                            <Trash2 class="mr-2 h-3.5 w-3.5" />
                            Clear recent searches
                        </CommandItem>
                    </CommandGroup>

                    <div v-if="showPopularSearches" class="px-3 py-4">
                        <NoResults
                            variant="compact"
                            title="Search creators"
                            message="Start with a popular creator or type a name."
                        >
                            <template #icon>
                                <div class="mb-2 inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500">
                                    <Sparkles class="h-4 w-4" />
                                </div>
                            </template>
                        </NoResults>
                        <CommandGroup heading="Popular creators" class="mt-1 p-0">
                            <CommandItem
                                v-for="popularSearch in POPULAR_CREATOR_SEARCHES"
                                :key="popularSearch"
                                :value="popularSearch"
                                class="rounded px-2 py-2 data-[highlighted]:bg-slate-100"
                                @select="selectSearchTerm($event, popularSearch)"
                            >
                                <Sparkles class="mr-2 h-4 w-4 text-slate-400" />
                                <span class="text-sm text-slate-700">{{ popularSearch }}</span>
                            </CommandItem>
                        </CommandGroup>
                    </div>

                    <div v-if="showMinimumPrompt" class="px-4 py-3 text-sm text-slate-500">
                        Type at least {{ LOCAL_SEARCH_MIN_CHARS }} characters to search creators.
                    </div>

                    <GlobalSearchResultsList
                        v-if="showResults"
                        :query="query"
                        :visible-results="visibleResults"
                        :pending-results="pendingResults"
                        :is-initial-loading="isInitialLoading"
                        :is-looking-for-creators="isLookingForCreators"
                        :can-search-more-creators="canSearchMoreCreators"
                        :no-creators-found="noCreatorsFound"
                        @select-creator="emit('selectCreator', $event)"
                        @search-more-creators="emit('searchMoreCreators')"
                        @show-more-results="emit('showMoreResults')"
                    />
                </CommandList>
            </PopoverContent>
        </Popover>
    </Command>
</template>
